/**
 * RetentionPage - Fila de decisão de e-mails (Plano B, Task 8)
 *
 * Migrada para o Design System (padrão-lista, ref `UsersPage`):
 *  - Toolbar 3 zonas (breadcrumb + contador · busca · Filtros).
 *  - Filtros num popover (tema claro fixo) → chips removíveis na filterbar.
 *  - Tabela responsiva (`RetentionList`) + painel de leitura (`RetentionReadingPane`)
 *    aberto ao clicar numa linha. Aprovar cria o ticket e autoriza o contato;
 *    Bloquear descarta futuros e-mails do contato silenciosamente.
 */

import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';
import type { EmailRetentionQuery, EmailRetentionResponse, EmailRetentionStatus } from '@troia-v3/types';

import { Button, Drawer, FilterBottomSheet, FiltersButton, ListPageEmpty, ListPageLoading, ListPagePagination, PageToolbar, ToolbarSearch } from '@/shared/components/ui';
import { useConfirm } from '@/shared/components/ui/ConfirmProvider';
import { Icon } from '@/shared/icons';
import { usePermissions } from '@/hooks';
import { useChannelsDropdown } from '@/modules/channels/hooks/useChannelsDropdown';

import { RetentionList, type RetentionRowAction } from '../components/RetentionList';
import { RetentionReadingPane } from '../components/RetentionReadingPane';
import { CustomerSelectModal } from '../components/CustomerSelectModal';
import { useApproveRetention, useBlockRetention, useDiscardRetention, useRetentionList } from '../hooks/useRetention';

const STATUS_OPTIONS: { value: EmailRetentionStatus | ''; label: string }[] = [
  { value: '', label: 'Todos' },
  { value: 'pending', label: 'Pendente' },
  { value: 'approved', label: 'Aprovado' },
  { value: 'blocked', label: 'Bloqueado' },
  { value: 'discarded', label: 'Descartado' },
];

const STATUS_LABEL: Record<EmailRetentionStatus, string> = {
  pending: 'Pendente',
  approved: 'Aprovado',
  blocked: 'Bloqueado',
  discarded: 'Descartado',
};

/** 409 = outro admin já revisou (claim atômico do backend). */
function isConflict(error: unknown): boolean {
  return axios.isAxiosError(error) && error.response?.status === 409;
}

const PAGE_SIZE = 20;

export const RetentionPage: React.FC = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<EmailRetentionStatus | ''>('pending');
  const [channelFilter, setChannelFilter] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [configOpen, setConfigOpen] = useState(false);
  // Seleção em massa (#154) + modal de cliente do ••• por linha (#155).
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [customerModal, setCustomerModal] = useState<{ entryId: string; mode: 'approve' | 'trust'; contactName?: string } | null>(null);

  const confirm = useConfirm();
  const { hasModulePermission } = usePermissions();
  const canApprove = hasModulePermission('tickets', 'create');
  const canBlock = hasModulePermission('tickets', 'update');

  const approveMutation = useApproveRetention();
  const discardMutation = useDiscardRetention();
  const blockMutation = useBlockRetention();

  const query: EmailRetentionQuery = useMemo(
    () => ({
      page,
      limit: PAGE_SIZE,
      ...(statusFilter ? { status: statusFilter } : {}),
      ...(channelFilter ? { channelId: channelFilter } : {}),
    }),
    [page, statusFilter, channelFilter],
  );

  const { data, isLoading } = useRetentionList(query);
  const { data: channels } = useChannelsDropdown();

  const channelNameById = useMemo(() => {
    const map: Record<string, string> = {};
    (channels || []).forEach((channel) => {
      map[channel.id] = channel.name;
    });
    return map;
  }, [channels]);

  const items = useMemo(() => data?.items ?? [], [data]);

  // Busca client-side sobre a página carregada (o backend não expõe `search`
  // em `EmailRetentionQuery` — ver report). Mesmo espírito do sort client-side
  // adotado em `UsersPage`.
  const visibleItems = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return items;
    return items.filter(
      (e) =>
        e.from.toLowerCase().includes(term) ||
        (e.subject ?? '').toLowerCase().includes(term) ||
        (e.snippet ?? '').toLowerCase().includes(term),
    );
  }, [items, searchTerm]);

  // ── Seleção em massa + ações (#154/#155) ──
  const toggleSelect = (id: string) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const toggleAll = () =>
    setSelectedIds((prev) => {
      const allSel = visibleItems.length > 0 && visibleItems.every((e) => prev.has(e.id));
      return allSel ? new Set() : new Set(visibleItems.map((e) => e.id));
    });
  const clearSelection = () => setSelectedIds(new Set());

  const runDiscard = async (id: string) => {
    try {
      await discardMutation.mutateAsync(id);
      toast.success('Email descartado');
    } catch (error) {
      if (isConflict(error)) toast.info('Este e-mail já foi revisado por outro administrador.');
      else if (!(axios.isAxiosError(error) && [402, 403].includes(error.response?.status ?? 0))) toast.error('Erro ao descartar e-mail');
    }
  };
  const runBlock = async (id: string) => {
    try {
      await blockMutation.mutateAsync(id);
      toast.success('Contato bloqueado');
    } catch (error) {
      if (isConflict(error)) toast.info('Este e-mail já foi revisado por outro administrador.');
      else if (!(axios.isAxiosError(error) && [402, 403].includes(error.response?.status ?? 0))) toast.error('Erro ao bloquear contato');
    }
  };

  const handleRowAction = (action: RetentionRowAction, entry: EmailRetentionResponse) => {
    if (action === 'approve' || action === 'trust') {
      // ••• da linha não tem o contato carregado → sempre oferece o modal de
      // cliente (associar é opcional; "sem cliente" não sobrescreve vínculo existente).
      setCustomerModal({ entryId: entry.id, mode: action, contactName: entry.from });
    } else if (action === 'discard') {
      void runDiscard(entry.id);
    } else {
      void runBlock(entry.id);
    }
  };

  const confirmCustomerModal = async (customerId: string | null) => {
    if (!customerModal) return;
    const { entryId, mode } = customerModal;
    setCustomerModal(null);
    try {
      await approveMutation.mutateAsync({ id: entryId, trustContact: mode === 'trust', customerId });
      toast.success(mode === 'trust' ? 'Contato confiado — ticket criado' : 'Email aprovado — ticket criado');
    } catch (error) {
      if (isConflict(error)) toast.info('Este e-mail já foi revisado por outro administrador.');
      else if (!(axios.isAxiosError(error) && [402, 403].includes(error.response?.status ?? 0))) toast.error('Erro ao aprovar e-mail');
    }
  };

  // Bulk (loop sequencial; aprovar/confiar em massa NÃO abre modal de cliente —
  // associação por item é inviável em lote, decisão do dono). Tolera falha parcial.
  const runBulk = async (action: RetentionRowAction) => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    if (
      (action === 'discard' || action === 'block') &&
      !(await confirm({
        message: `${action === 'block' ? 'Bloquear' : 'Descartar'} ${ids.length} e-mail(s)?`,
        confirmButtonText: action === 'block' ? 'Bloquear' : 'Descartar',
        variant: 'danger',
      }))
    )
      return;
    let ok = 0;
    let failed = 0;
    for (const id of ids) {
      try {
        if (action === 'approve' || action === 'trust') await approveMutation.mutateAsync({ id, trustContact: action === 'trust' });
        else if (action === 'discard') await discardMutation.mutateAsync(id);
        else await blockMutation.mutateAsync(id);
        ok += 1;
      } catch {
        failed += 1;
      }
    }
    if (ok > 0) toast.success(`${ok} e-mail(s) processado(s)`);
    if (failed > 0) toast.error(`${failed} não processado(s)`);
    clearSelection();
  };

  const statusLabel = statusFilter ? STATUS_LABEL[statusFilter] : null;
  const channelLabel = channelFilter ? (channelNameById[channelFilter] ?? 'Canal') : null;
  const activeFilterCount = [statusLabel, channelLabel].filter(Boolean).length;

  const applyStatus = (value: EmailRetentionStatus | '') => {
    setStatusFilter(value);
    setPage(1);
    setFiltersOpen(false);
  };

  const applyChannel = (value: string) => {
    setChannelFilter(value);
    setPage(1);
    setFiltersOpen(false);
  };

  const clearAllFilters = () => {
    setStatusFilter('');
    setChannelFilter('');
    setPage(1);
    setFiltersOpen(false);
  };

  // Conteúdo dos filtros — reusado no popover desktop E no FilterBottomSheet mobile.
  const filterContent = (
    <>
      {/* Status */}
      <p className="px-2.5 pt-1.5 pb-1 text-[11px] font-bold uppercase tracking-wider" style={{ color: '#94a3b8' }}>
        Status
      </p>
      {STATUS_OPTIONS.map((opt) => {
        const active = statusFilter === opt.value;
        return (
          <button
            key={opt.value || 'all'}
            onClick={() => applyStatus(opt.value)}
            className="w-full flex items-center justify-between px-2.5 py-2 rounded-lg text-sm font-medium text-left"
            style={{ color: '#374151', backgroundColor: active ? '#f3e8ff' : 'transparent' }}
            data-testid={`retention-filter-status-${opt.value || 'all'}`}
          >
            {opt.label}
            {active && <Icon name="check" className="w-4 h-4" style={{ color: '#8b5cf6' }} />}
          </button>
        );
      })}

      {/* Canal */}
      <p className="px-2.5 pt-2.5 pb-1 text-[11px] font-bold uppercase tracking-wider" style={{ color: '#94a3b8' }}>
        Canal
      </p>
      <button
        onClick={() => applyChannel('')}
        className="w-full flex items-center justify-between px-2.5 py-2 rounded-lg text-sm font-medium text-left"
        style={{ color: '#374151', backgroundColor: !channelFilter ? '#f3e8ff' : 'transparent' }}
        data-testid="retention-filter-channel-all"
      >
        Todos os canais
        {!channelFilter && <Icon name="check" className="w-4 h-4" style={{ color: '#8b5cf6' }} />}
      </button>
      {(channels || []).map((channel) => {
        const active = channelFilter === channel.id;
        return (
          <button
            key={channel.id}
            onClick={() => applyChannel(channel.id)}
            className="w-full flex items-center justify-between px-2.5 py-2 rounded-lg text-sm font-medium text-left"
            style={{ color: '#374151', backgroundColor: active ? '#f3e8ff' : 'transparent' }}
            data-testid={`retention-filter-channel-${channel.id}`}
          >
            <span className="truncate">{channel.name}</span>
            {active && <Icon name="check" className="w-4 h-4 flex-none" style={{ color: '#8b5cf6' }} />}
          </button>
        );
      })}
    </>
  );

  return (
    <div className="h-full flex flex-col" style={{ backgroundColor: 'var(--theme-page-background)' }}>
      {/* ── Toolbar 3 zonas (contrato DS 3.3) ── */}
      <PageToolbar
        left={
          <>
            {/* Esquerda: breadcrumb + contador */}
          <nav className="flex items-center gap-2 min-w-0">
            <button
              onClick={() => navigate('/helpdesk/tickets')}
              className="text-sm font-medium truncate hidden sm:inline"
              style={{ color: 'var(--theme-muted-text)' }}
              data-testid="retention-back-button"
            >
              Help Desk
            </button>
            <Icon
              name="chevron-right"
              className="w-3.5 h-3.5 hidden sm:inline"
              style={{ color: 'var(--theme-muted-text)', opacity: 0.6 }}
            />
            <span className="font-semibold text-base truncate" style={{ color: 'var(--theme-text)' }}>
              Retenção de e-mails
            </span>
          </nav>
          {data?.total != null && (
            <span
              className="text-xs font-semibold rounded-full px-2 py-0.5 tabular-nums flex-none"
              style={{ backgroundColor: 'var(--theme-surface-sunken)', color: 'var(--theme-muted-text)' }}
            >
              {data.total}
            </span>
          )}
          </>
        }
        center={
          /* Centro: busca (sempre centralizada — F17-F22: ToolbarSearch) */
          <ToolbarSearch
            value={searchTerm}
            onChange={setSearchTerm}
            placeholder="Buscar por remetente ou assunto…"
            shrinkClass="hd-search"
            data-testid="retention-search"
          />
        }
        right={
          /* Direita (ordem canônica F15): Filtros → separador → CTA primário */
          <div className="flex items-center gap-2 flex-none">
            <div className="relative">
            <FiltersButton
              count={activeFilterCount}
              active={filtersOpen}
              onClick={() => setFiltersOpen((v) => !v)}
              data-testid="retention-filters"
            />
            {filtersOpen && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setFiltersOpen(false)} />
                <div
                  className="absolute right-0 top-full mt-2 z-50 w-64 rounded-xl p-2 max-h-[70vh] overflow-y-auto"
                  style={{ backgroundColor: '#ffffff', border: '1px solid #e5e7eb', boxShadow: '0 4px 24px rgba(0,0,0,.12)' }}
                  data-testid="retention-filters-panel"
                >
                  {filterContent}
                </div>
              </>
            )}
            </div>
            <span className="w-px h-[22px] flex-none" style={{ backgroundColor: 'var(--theme-input-border)' }} />
            {/* F01-F03: primário via Button shared; rótulo colapsa pela chain hd-tb (.hd-prim) */}
            <Button
              variant="primary"
              icon="settings"
              collapsibleLabel
              className="hd-prim"
              onClick={() => setConfigOpen(true)}
              data-testid="retention-config"
            >
              Configurar retenção
            </Button>
          </div>
        }
        mobile={
          /* `.m-sub` do mockup (#157): busca (ToolbarSearch) + Filtros 38px */
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <ToolbarSearch
              value={searchTerm}
              onChange={setSearchTerm}
              placeholder="Buscar…"
              data-testid="retention-search-mobile"
            />
            <button
              onClick={() => setFiltersOpen(true)}
              className="flex items-center gap-1.5 h-[38px] px-3 rounded-[10px] text-sm font-medium flex-none"
              style={{ border: '1px solid var(--theme-input-border)', backgroundColor: 'var(--theme-card-background)', color: 'var(--theme-card-text)' }}
              data-testid="retention-filters-mobile"
            >
              <Icon name="filter" className="w-3.5 h-3.5" style={{ color: 'var(--theme-muted-text)' }} />
              Filtros
              {activeFilterCount > 0 && (
                <span className="inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full text-[10px] font-bold text-white" style={{ backgroundColor: 'var(--theme-primary)' }}>
                  {activeFilterCount}
                </span>
              )}
            </button>
          </div>
        }
        chain="hd-tb"
      />

      {/* ── Filterbar (chips) — some quando vazia ── */}
      {activeFilterCount > 0 && (
        <div
          className="flex-none flex items-center flex-wrap gap-2 px-4 py-2"
          style={{ backgroundColor: 'var(--theme-header-background)', borderBottom: '1px solid var(--theme-header-border)' }}
        >
          {statusLabel && (
            <span
              className="inline-flex items-center gap-1 h-[26px] pl-2.5 pr-1 rounded-full text-xs font-semibold"
              style={{ backgroundColor: 'var(--theme-primary-background)', color: 'var(--theme-primary)' }}
            >
              Status: {statusLabel}
              <button
                onClick={() => applyStatus('')}
                className="w-[18px] h-[18px] rounded-full inline-flex items-center justify-center opacity-60 hover:opacity-100"
                aria-label="Remover filtro de status"
                data-testid="retention-chip-status-remove"
              >
                <Icon name="x" className="w-3 h-3" />
              </button>
            </span>
          )}
          {channelLabel && (
            <span
              className="inline-flex items-center gap-1 h-[26px] pl-2.5 pr-1 rounded-full text-xs font-semibold"
              style={{ backgroundColor: 'var(--theme-primary-background)', color: 'var(--theme-primary)' }}
            >
              Canal: {channelLabel}
              <button
                onClick={() => applyChannel('')}
                className="w-[18px] h-[18px] rounded-full inline-flex items-center justify-center opacity-60 hover:opacity-100"
                aria-label="Remover filtro de canal"
                data-testid="retention-chip-channel-remove"
              >
                <Icon name="x" className="w-3 h-3" />
              </button>
            </span>
          )}
          <button onClick={clearAllFilters} className="text-xs font-semibold" style={{ color: 'var(--theme-muted-text)' }} data-testid="retention-clear-filters">
            Limpar tudo
          </button>
        </div>
      )}

      {/* ── Conteúdo ── */}
      <div className="flex-1 overflow-auto">
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 md:px-8 py-6">
          {isLoading && <ListPageLoading message="Carregando e-mails retidos..." />}

          {!isLoading && visibleItems.length === 0 && (
            <ListPageEmpty
              icon="inbox"
              message={searchTerm ? 'Nenhum e-mail encontrado' : 'Nenhum e-mail nesta fila'}
              description={
                searchTerm || activeFilterCount > 0
                  ? 'Tente ajustar a busca ou os filtros.'
                  : 'Quando um e-mail de um contato ainda não autorizado chegar, ele aparece aqui para aprovação.'
              }
            />
          )}

          {/* Bulk bar (#154) — lilás, aparece quando há seleção. */}
          {selectedIds.size > 0 && (
            <div
              className="flex items-center gap-3 rounded-lg mb-3 font-semibold px-3.5 py-2"
              style={{ backgroundColor: 'var(--theme-primary-background)', color: 'var(--theme-primary)' }}
              data-testid="retention-bulkbar"
            >
              <span className="text-[13px] tabular-nums">{selectedIds.size} selecionado(s)</span>
              <span className="flex-1" />
              {canApprove && (
                <>
                  <button onClick={() => void runBulk('approve')} className="inline-flex items-center gap-1.5 text-sm font-semibold hover:underline" data-testid="retention-bulk-approve">
                    <Icon name="check" className="w-3.5 h-3.5" /> Aprovar
                  </button>
                  <button onClick={() => void runBulk('trust')} className="inline-flex items-center gap-1.5 text-sm font-semibold hover:underline" data-testid="retention-bulk-trust">
                    <Icon name="user-check" className="w-3.5 h-3.5" /> Confiar
                  </button>
                </>
              )}
              {canBlock && (
                <>
                  <button onClick={() => void runBulk('discard')} className="inline-flex items-center gap-1.5 text-sm font-semibold hover:underline" data-testid="retention-bulk-discard">
                    <Icon name="x-circle" className="w-3.5 h-3.5" /> Descartar
                  </button>
                  <button onClick={() => void runBulk('block')} className="inline-flex items-center gap-1.5 text-sm font-semibold hover:underline" data-testid="retention-bulk-block">
                    <Icon name="shield" className="w-3.5 h-3.5" /> Bloquear
                  </button>
                </>
              )}
              <button onClick={clearSelection} className="text-sm font-semibold hover:underline" data-testid="retention-bulk-clear">Limpar</button>
            </div>
          )}

          {!isLoading && visibleItems.length > 0 && (
            <RetentionList
              entries={visibleItems}
              channelNameById={channelNameById}
              selectedId={selectedId}
              onSelect={setSelectedId}
              selectedIds={selectedIds}
              onToggleSelect={toggleSelect}
              onToggleAll={toggleAll}
              onRowAction={handleRowAction}
              canApprove={canApprove}
              canBlock={canBlock}
            />
          )}

          {!isLoading && visibleItems.length > 0 && data && (
            <div className="mt-6">
              <ListPagePagination
                currentPage={data.page}
                totalPages={data.totalPages}
                onPageChange={setPage}
                totalItems={data.total}
                pageSize={PAGE_SIZE}
                itemLabel="casos"
              />
            </div>
          )}
        </div>
      </div>

      {/* Drawer de configuração de retenção (conteúdo placeholder, como no mockup) */}
      <Drawer
        isOpen={configOpen}
        onClose={() => setConfigOpen(false)}
        size="md"
        icon={<Icon name="settings" className="w-5 h-5" />}
        title="Configurar retenção"
      >
        <ListPageEmpty
          icon="settings"
          message="Configuração de retenção"
          description="Em breve você poderá definir regras de retenção por canal, remetentes confiáveis e bloqueios automáticos."
        />
      </Drawer>

      <RetentionReadingPane
        entryId={selectedId}
        isOpen={selectedId !== null}
        onClose={() => setSelectedId(null)}
      />

      {/* Modal de cliente do ••• por linha (aprovar/confiar) */}
      <CustomerSelectModal
        isOpen={customerModal !== null}
        actionLabel={customerModal?.mode === 'trust' ? 'Confiar no contato' : 'Aprovar e-mail'}
        contactName={customerModal?.contactName}
        isSubmitting={approveMutation.isPending}
        onConfirm={(customerId) => void confirmCustomerModal(customerId)}
        onClose={() => setCustomerModal(null)}
      />

      {/* Filtros mobile (#157) — mesmo conteúdo do popover desktop */}
      <FilterBottomSheet isOpen={filtersOpen} onClose={() => setFiltersOpen(false)} data-testid="retention-filters-sheet">
        {filterContent}
      </FilterBottomSheet>

      {/* FAB mobile (#157) — "Configurar retenção" (ação primária; Retenção não tem "criar") */}
      <button
        type="button"
        onClick={() => setConfigOpen(true)}
        className="sm:hidden fixed right-4 bottom-[84px] z-40 w-[54px] h-[54px] rounded-[17px] flex items-center justify-center text-white shadow-xl"
        style={{ backgroundColor: 'var(--theme-primary)', boxShadow: '0 6px 18px rgba(139,92,246,.4)' }}
        aria-label="Configurar retenção"
        data-testid="retention-fab"
      >
        <Icon name="settings" className="w-6 h-6" />
      </button>
    </div>
  );
};

RetentionPage.displayName = 'RetentionPage';
